/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const JSG = require('../../JSG');
const { Reference } = require('@cedalo/parser');

const CellRange = require('../model/CellRange');
/**
 * An instance of this class is used to reference a certain property of a specified item.</br>
 * Note: item properties could be structured into different objects. That is why the third parameter
 * is required. The projectObject actually holds the referenced property.
 *
 * @class SheetReference
 * @constructor
 * @param {SheetModel} sheet Sheetmodel
 * @param {String} [reference] The reference text.
 */
module.exports = class SheetReference extends Reference {
	static fromString(sheet, reference) {
		if (reference === undefined) {
			return undefined;
		}

		let ret;

		const range = CellRange.parse(reference, sheet);
		if (range) {
			ret = new SheetReference(sheet);
			ret._range = range;
			return ret;
		}

		if (sheet) {
			const data = sheet.cells;
			if (data.names.some((name) => {
				if (name.getName().toUpperCase() === reference) {
					ret = new SheetReference(sheet);
					ret._name = name;
					return true;
				}
				return false;
			})) {
				return ret;
			}
			sheet.shapes.shapes.some(shape => {
				if (shape.name && shape.name.v.toUpperCase() === reference) {
					ret = new SheetReference(sheet);
					ret._shape = shape;
					return true;
				}
				return false;
			});

		}

		if (JSG.sheetNames.names.some((name) => {
			if (name.getName().toUpperCase() === reference) {
				ret = new SheetReference(sheet);
				ret._name = name;
				this._name = name;
				return true;
			}
			return false;
		})) {
			return ret;
		}

		return ret;
	}

	constructor(sheet) {
		super();

		this._sheet = sheet;
	}

	isValid() {
		return this._name || this._shape || (this._range && this._range.isValid());
	}

	isResolved() {
		return true;
	}

	/**
	 * Creates a copy of this SheetReference instance.
	 *
	 * @method copy
	 * @return {SheetReference} A copy of this SheetReference instance.
	 */
	copy() {
		const copy = new SheetReference(this._sheet);

		if (this._range) {
			copy._range = this._range.copy();
		}
		if (this._name) {
			copy._name = this._name;
		}
		if (this._shape) {
			copy._shape = this._shape;
		}

		return copy;
	}

	getRange() {
		return this._range;
	}

	/**
	 * Returns the value of referenced attribute. </br>
	 *
	 * @method getValue
	 * @return {Object} The value of referenced attribute.
	 */
	getValue() {
		return '#CALC';
	}

	get value() {
		return this.getValue();
	}

	getPropertyString(params) {
		if (this._name) {
			return this._name.getName();
		}

		if (this._range) {
			return this._range.toString(params);
		}

		if (this._shape) {
			const str = this._shape.name.v;
			// let sheet = this._shapeItem.getParent();
			// while (sheet && !(sheet.isStreamSheet)) {
			// 	sheet = sheet.getParent();
			// }
			// if (sheet) {
			if (params && params.item) {
				// eslint-disable-next-line no-console
				console.log('getPropertyString with Item');
			}
				if (params && (params.forceName || params.sheet !== this._sheet)) {
					return `${this._sheet.name}!${str}`;
				}
				return str;
			// }
		}

		return '#REF!';
	}

	isEqualTo(operand) {
		if (!operand || this.type !== operand.type) {
			return false;
		}

		if (this._name) {
			return this._name.getName() === operand._name.getName() &&
				this._name.getFormula() === operand._name.getFormula();
		}

		if (this._range) {
			return this._range.isEqualTo(operand._range);
		}

		if (this._shape) {
			return this._shape.id === operand._shape.id;
		}

		return false;
	}

	toString(params) {
		if (this.isResolved()) {
			return this.getPropertyString(params);
		}
		// not  resolved => simply return raw string...
		return this._str;
	}
}
